import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getComponentSortRank } from '@/types/database';
import {
  Search, Download, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, ArrowUpDown, Calendar,
  FileSpreadsheet, ChevronDown, ChevronRight as ChevronRightIcon,
  AlertCircle, X, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import {
  isSuperAdmin as isSuperAdminRole,
  isTechnician as isTechnicianRole,
  canViewCosts as canViewCostsRole,
} from '@/lib/roles';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import { toast } from 'sonner';
import { writeAuditLog } from '@/lib/audit';
import { fifoReallocate } from '@/lib/fifoReallocate';
import { filterPaosBySearch } from '@/lib/paoSearch';

type SortField = 'completed_date' | 'aso' | 'build_sku' | 'build_name' | 'technician_name' | 'total_cost';
type SortOrder = 'asc' | 'desc';

interface AssemblyOrder {
  id: string; // ✅ Added for unique keys
  aso: string;
  build_sku: string;
  build_description: string;
  technician_name: string;
  total_cost: number;
  per_build_cost: number;
  quantity: number;
  completed_date: string;
  created_at: string;
  reservation_id: string;
  reservation_code: string | null;
  to_number: string | null;
  serial_number: string | null;
  serial_numbers: Record<string, string> | null;
  /**
   * Part numbers whose line matched the current search, so the row can open on
   * the component that caused the hit. Empty unless the search matched a
   * component — an order found by its own number or SKU is not tagged.
   */
  matched_components: string[];
  components: {
    part_type: string;
    part_number: string;
    description: string;
    quantity: number;
    unit_cost: number;
    line_total: number;
  }[];
}

function calculateWorkdays(startDate: Date, endDate: Date): number {
  let count = 0;
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
}

function calculateBuildDuration(startDate: string | null, endDate: string | null): string | null {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  
  // ✅ Don't calculate if end is before start (invalid data)
  if (end < start) return null;
  
  const workdays = calculateWorkdays(start, end);
  if (workdays < 0) return null;
  if (workdays === 0) return '< 1 day';
  if (workdays === 1) return '1 day';
  return `${workdays} days`;
}

export function StockIOPage() {
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | 'all'>(25);
  const [sortField, setSortField] = useState<SortField>('completed_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [deleteModal, setDeleteModal] = useState<AssemblyOrder | null>(null);

  // Detect role — managers see managed technicians' PAOs, technicians see only their own
  const { data: myContext, isLoading: contextLoading } = useQuery({
    queryKey: ['stockio-user-context'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { role: null, techId: null, managedIds: null };
      const { data } = await supabase
        .from('technicians')
        .select('id, role')
        .eq('user_id', user.id)
        .single();
      if (!data) return { role: null, techId: null, managedIds: null };
      return { role: data.role, techId: data.id, managedIds: null };
    },
  });

  const isManager    = myContext?.role === 'manager';
  const isSuperAdmin = isSuperAdminRole(myContext?.role);
  const isTechnician = isTechnicianRole(myContext?.role);
  // Was `!isTechnician`. Identical in effect - this route admits only
  // super_admin, admin, manager and technician - but the allow-list does not
  // depend on that guard staying as it is. Add marketing to allowedRoles one
  // day and a deny-list starts showing costs; this does not.
  const canViewCosts = canViewCostsRole(myContext?.role);
  const managedIds   = myContext?.managedIds ?? null;

  const queryClient = useQueryClient();

  type DeleteMode = 'blank_slate' | 'restore';

  const deletePAO = useMutation({
    mutationFn: async ({ order, mode }: { order: AssemblyOrder; mode: DeleteMode }) => {
      const paoQty = order.quantity || 1;

      // 1. Restore component stock (reverse the deductions made when posting)
      if (order.components?.length) {
        const skus = order.components.map(c => c.part_number).filter(Boolean);
        if (skus.length) {
          const { data: comps } = await supabase
            .from('components')
            .select('part_number, total_qty, available_qty, reserved_qty')
            .in('part_number', skus);

          if (comps?.length) {
            const compMap = Object.fromEntries(
              comps.map((c: any) => [c.part_number, { total: c.total_qty ?? 0, available: c.available_qty ?? 0, reserved: c.reserved_qty ?? 0 }])
            );
            await Promise.all(
              order.components.map(item => {
                // item.quantity is per-build; scale by paoQty to get the total originally consumed
                const totalConsumed = item.quantity * paoQty;
                const cur = compMap[item.part_number] ?? { total: 0, available: 0, reserved: 0 };
                if (mode === 'blank_slate') {
                  // Posting consumed from reserved. Reservation is being erased.
                  // Restore total_qty and available_qty (stock returns to free stock).
                  return supabase.from('components').update({
                    total_qty: cur.total + totalConsumed,
                    available_qty: cur.available + totalConsumed,
                  }).eq('part_number', item.part_number);
                } else {
                  // Posting consumed from reserved. Reservation is being re-queued.
                  // Restore total_qty and reserved_qty (stock returns to reserved pool).
                  return supabase.from('components').update({
                    total_qty: cur.total + totalConsumed,
                    reserved_qty: cur.reserved + totalConsumed,
                  }).eq('part_number', item.part_number);
                }
              })
            );
          }
        }
      }

      // 2. Update linked reservation based on chosen mode
      let reservationAction = 'none';
      if (order.reservation_id) {
        const { data: res } = await supabase
          .from('reservations')
          .select('id, reservation_code, quantity, quantity_fulfilled, status')
          .eq('id', order.reservation_id)
          .single();

        if (res) {
          if (mode === 'blank_slate') {
            // Remove these units from the reservation entirely
            const newQty = res.quantity - paoQty;
            const newFulfilled = Math.max(0, (res.quantity_fulfilled || 0) - paoQty);

            if (newQty <= 0) {
              // All units came from this PAO — delete the reservation
              await supabase.from('reservation_components').delete().eq('reservation_id', res.id);
              await supabase.from('reservations').delete().eq('id', res.id);
              reservationAction = 'deleted';

              await writeAuditLog({
                action: 'reservation_deleted_via_pao',
                entity_type: 'reservation',
                entity_id: res.id,
                entity_label: res.reservation_code,
                old_value: { quantity: res.quantity, quantity_fulfilled: res.quantity_fulfilled, status: res.status },
                notes: `Blank slate: reservation deleted because PAO ${order.aso} (${paoQty} units) was deleted. Original reservation had ${res.quantity} units.`,
              });
            } else {
              await supabase
                .from('reservations')
                .update({ quantity: newQty, quantity_fulfilled: newFulfilled })
                .eq('id', res.id);
              reservationAction = 'reduced';

              await writeAuditLog({
                action: 'reservation_reduced_via_pao',
                entity_type: 'reservation',
                entity_id: res.id,
                entity_label: res.reservation_code,
                old_value: { quantity: res.quantity, quantity_fulfilled: res.quantity_fulfilled, status: res.status },
                new_value: { quantity: newQty, quantity_fulfilled: newFulfilled },
                notes: `Blank slate: quantity reduced from ${res.quantity} to ${newQty} because PAO ${order.aso} (${paoQty} units) was deleted.`,
              });
            }
          } else {
            // Restore — keep original quantity, roll back fulfillment so units re-enter the queue
            const newFulfilled = Math.max(0, (res.quantity_fulfilled || 0) - paoQty);

            await supabase
              .from('reservations')
              .update({ quantity_fulfilled: newFulfilled })
              .eq('id', res.id);
            reservationAction = 'restored';

            // Give this reservation's own allocation ledger back the stock that was
            // consumed at posting time (ProductionPage decrements allocated_qty on post;
            // nothing else ever adds it back), then let FIFO re-split allocated/expected
            // against current stock for every affected SKU across all active reservations.
            if (order.components?.length) {
              const { data: rcRows } = await supabase
                .from('reservation_components')
                .select('id, component_sku, allocated_qty')
                .eq('reservation_id', res.id);
              await Promise.all(order.components.map(item => {
                const rc = rcRows?.find((r: any) => r.component_sku === item.part_number);
                if (!rc) return Promise.resolve();
                return supabase.from('reservation_components').update({
                  allocated_qty: (rc.allocated_qty ?? 0) + item.quantity * paoQty,
                }).eq('id', rc.id);
              }));
              const skus = [...new Set(order.components.map(c => c.part_number).filter(Boolean))];
              await fifoReallocate(skus);
            }

            await writeAuditLog({
              action: 'reservation_restored_via_pao',
              entity_type: 'reservation',
              entity_id: res.id,
              entity_label: res.reservation_code,
              old_value: { quantity: res.quantity, quantity_fulfilled: res.quantity_fulfilled, status: res.status },
              new_value: { quantity: res.quantity, quantity_fulfilled: newFulfilled },
              notes: `Restore: fulfillment rolled back from ${res.quantity_fulfilled} to ${newFulfilled} because PAO ${order.aso} (${paoQty} units) was deleted. Reservation re-queued for building.`,
            });
          }
        }
      }

      // 3. Delete the PAO and its components
      await supabase.from('assembly_order_components').delete().eq('ao_id', order.id);
      const { error } = await supabase.from('assembly_orders').delete().eq('id', order.id);
      if (error) throw error;

      // 4. Audit log for the PAO deletion itself
      await writeAuditLog({
        action: 'pao_deleted',
        entity_type: 'posted_assembly_order',
        entity_id: order.id,
        entity_label: order.aso,
        old_value: {
          build_sku: order.build_sku,
          quantity: paoQty,
          technician: order.technician_name,
          total_cost: order.total_cost,
          completed_date: order.completed_date,
          reservation_id: order.reservation_id || null,
          reservation_code: order.reservation_code || null,
        },
        notes: `PAO ${order.aso} deleted by super admin (mode: ${mode}). Reservation: ${reservationAction}. Stock restored for ${order.components?.length || 0} component lines.`,
      });

      return { aso: order.aso, reservationAction, mode };
    },
    onSuccess: (result) => {
      setDeleteModal(null);
      queryClient.invalidateQueries({ queryKey: ['posted-assembly-orders'] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservations-enriched'] });
      queryClient.invalidateQueries({ queryKey: ['reservation'] });
      queryClient.invalidateQueries({ queryKey: ['production-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-stock-readiness'] });
      queryClient.invalidateQueries({ queryKey: ['components'] });

      const resMsg =
        result.reservationAction === 'deleted'  ? ' Reservation deleted.' :
        result.reservationAction === 'reduced'  ? ' Reservation quantity reduced.' :
        result.reservationAction === 'restored' ? ' Reservation re-queued for building.' :
        '';
      toast.success(`${result.aso} deleted.${resMsg} Stock restored.`);
    },
    onError: (e: any) => {
      toast.error(`Delete failed: ${e.message}`);
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['posted-assembly-orders', search, dateFrom, dateTo, page, pageSize, sortField, sortOrder, managedIds, myContext?.techId],
    queryFn: async () => {
      console.log('📊 Fetching posted assembly orders...');

      // Step 1: Get PAO records (only completed/posted builds)
      let paoQuery = supabase
        .from('assembly_orders')
        .select('*')
        .in('status', ['Posted', 'Completed']);

      // Technicians only see their own PAOs
      if (myContext?.role === 'technician' && myContext.techId) {
        paoQuery = paoQuery.eq('technician_id', myContext.techId);
      }

      if (search) {
        // filtering done client-side after technician names are resolved
      }

      if (dateFrom) {
        paoQuery = paoQuery.gte('completed_date', dateFrom);
      }

      if (dateTo) {
        paoQuery = paoQuery.lte('completed_date', dateTo);
      }

      const { data: paoData, error: paoError } = await paoQuery;
      
      if (paoError) {
        console.error('❌ PAO query error:', paoError);
        throw paoError;
      }

      console.log(`✅ Found ${paoData?.length || 0} PAO records`);

      if (!paoData || paoData.length === 0) {
        return {
          orders: [],
          totalRecords: 0,
          totalPages: 1,
        };
      }

      // Step 2: Get build descriptions
      const buildSkus = [...new Set(paoData.map(p => p.build_sku).filter(Boolean))];
      const buildsMap: Record<string, string> = {};
      
      if (buildSkus.length > 0) {
        const { data: buildsData } = await supabase
          .from('builds')
          .select('sku, description')
          .in('sku', buildSkus);

        buildsData?.forEach(b => {
          buildsMap[b.sku] = b.description;
        });
      }

      // Step 2b: Get technician names
      const techIds = [...new Set(paoData.map(p => p.technician_id).filter(Boolean))];
      const techNamesMap: Record<string, string> = {};
      if (techIds.length > 0) {
        const { data: techsData } = await supabase
          .from('technicians')
          .select('id, name, alias')
          .in('id', techIds);
        techsData?.forEach(t => {
          techNamesMap[t.id] = t.alias || t.name;
        });
      }

      // Step 3: Get components from posted_assembly_order_components (historical costs)
      const paoIds = paoData.map(p => p.id).filter(Boolean);
      const componentsMap: Record<string, any[]> = {};

      // Fetch reservation codes
      const reservationIds = [...new Set(paoData.map(p => p.reservation_id).filter(Boolean))];
      const reservationCodesMap: Record<string, string> = {};
      if (reservationIds.length > 0) {
        for (let i = 0; i < reservationIds.length; i += 100) {
          const { data: resRows } = await supabase
            .from('reservations')
            .select('id, reservation_code')
            .in('id', reservationIds.slice(i, i + 100));
          (resRows || []).forEach((r: any) => {
            if (r.reservation_code) reservationCodesMap[r.id] = r.reservation_code;
          });
        }
      }

      if (paoIds.length > 0) {
        const CHUNK_SIZE = 100;
        for (let i = 0; i < paoIds.length; i += CHUNK_SIZE) {
          const chunk = paoIds.slice(i, i + CHUNK_SIZE);
          const { data: componentsData, error: compError } = await supabase
            .from('assembly_order_components')
            .select('*')
            .in('ao_id', chunk);

          if (compError) {
            console.error('Error fetching components chunk:', compError);
            continue;
          }

          componentsData?.forEach(comp => {
            if (!componentsMap[comp.ao_id]) {
              componentsMap[comp.ao_id] = [];
            }
            componentsMap[comp.ao_id].push(comp);
          });
        }
      }

      // Step 4: Get component descriptions from components table - BATCHED
      const componentSkus = [...new Set(
        Object.values(componentsMap).flat().map(c => c.component_sku).filter(Boolean)
      )];
      
      
      const componentDescriptionsMap: Record<string, string> = {};
      
      if (componentSkus.length > 0) {
        const CHUNK_SIZE = 100;
        for (let i = 0; i < componentSkus.length; i += CHUNK_SIZE) {
          const chunk = componentSkus.slice(i, i + CHUNK_SIZE);
          const { data: componentsDescData } = await supabase
            .from('components')
            .select('part_number, description')
            .in('part_number', chunk);

          componentsDescData?.forEach(c => {
            componentDescriptionsMap[c.part_number] = c.description;
          });
        }
      }

      // Step 5: Transform data
      const orders: AssemblyOrder[] = paoData.map(pao => {
        const components = componentsMap[pao.id] || [];

        const aoQty = pao.quantity || 1;
        const adjustedComponents = components.map(c => {
          const totalQty  = c.component_qty   || 0;
          const perBuildQty = totalQty / aoQty; // show per-build quantity in expanded view
          const unitCost  = c.cost_unit_ex_vat  || 0;
          const lineTotal = perBuildQty * unitCost; // per-build line total
          return {
            part_type:   c.component_type || 'COMPONENT',
            part_number: c.component_sku || '',
            description: componentDescriptionsMap[c.component_sku] || c.component_sku || '',
            quantity:    perBuildQty,
            unit_cost:   unitCost,
            line_total:  lineTotal,
          };
        });

        const totalCost = pao.total_cost_ex_vat || adjustedComponents.reduce((sum, c) => sum + c.line_total, 0);
        const perBuildCost = totalCost / (pao.quantity || 1);

        return {
          id: pao.id,
          aso: pao.order_number || '',
          build_sku: pao.build_sku || '',
          build_description: buildsMap[pao.build_sku] || pao.build_sku || '',
          technician_name: techNamesMap[pao.technician_id] || '—',
          total_cost: totalCost,
          per_build_cost: perBuildCost,
          completed_date: pao.completed_date || '',
          created_at: pao.created_at || '',
          reservation_id: pao.reservation_id || '',
          reservation_code: pao.reservation_id ? (reservationCodesMap[pao.reservation_id] || null) : null,
          to_number: pao.to_number,
          serial_number: pao.serial_number || null,
          serial_numbers: pao.serial_numbers || null,
          quantity: pao.quantity || 1,
          matched_components: [],
          components: adjustedComponents,
        };
      });

      // Step 6: Sort
      orders.sort((a, b) => {
        let aVal: any, bVal: any;
        switch (sortField) {
          case 'completed_date':
            aVal = new Date(a.completed_date).getTime();
            bVal = new Date(b.completed_date).getTime();
            break;
          case 'aso':
            aVal = a.aso;
            bVal = b.aso;
            break;
          case 'build_sku':
            aVal = a.build_sku;
            bVal = b.build_sku;
            break;
          case 'build_name':
            aVal = (a.build_description || '').toLowerCase();
            bVal = (b.build_description || '').toLowerCase();
            break;
          case 'technician_name':
            aVal = (a.technician_name || '').toLowerCase();
            bVal = (b.technician_name || '').toLowerCase();
            break;
          case 'total_cost':
            aVal = a.total_cost;
            bVal = b.total_cost;
            break;
          default:
            aVal = a.completed_date;
            bVal = b.completed_date;
        }
        
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      // Step 6.5: Filter client-side — technician names and component lines are
      // both resolved above, so neither is filterable server-side without a join.
      const filteredOrders = filterPaosBySearch(orders, search);

      // Step 7: Pagination
      const totalRecords = filteredOrders.length;
      const paginatedOrders = pageSize === 'all' 
        ? filteredOrders 
        : filteredOrders.slice((page - 1) * pageSize, page * pageSize);

      console.log(`✅ Returning ${paginatedOrders.length} orders (${totalRecords} total)`);

      return {
        orders: paginatedOrders,
        totalRecords,
        totalPages: pageSize === 'all' ? 1 : Math.ceil(totalRecords / pageSize),
      };
    },
  });

  // Summary stats - separate query so they always reflect all data, not just current page
  const { data: summaryData } = useQuery({
    queryKey: ['posted-assembly-summary', dateFrom, dateTo, managedIds],
    queryFn: async () => {
      let query = supabase
        .from('assembly_orders')
        .select('id, reservation_id, quantity, total_cost_ex_vat')
        .in('status', ['Posted', 'Completed']);

      if (dateFrom) query = query.gte('completed_date', dateFrom);
      if (dateTo) query = query.lte('completed_date', dateTo);

      const { data: paoRows } = await query;
      if (!paoRows?.length) return { orderCount: 0, totalUnits: 0, totalCost: 0 };

      const totalUnits = paoRows.reduce((sum, p) => sum + (p.quantity || 1), 0);
      const totalCost = paoRows.reduce((sum, p) => sum + (p.total_cost_ex_vat || 0), 0);

      return {
        orderCount: paoRows.length,
        totalUnits,
        totalCost,
      };
    },
  });

  const toggleExpanded = (id: string) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortHeader = ({ field, children, className = '' }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <th 
      className={cn("text-left p-3 font-medium cursor-pointer hover:bg-muted/70 select-none", className)}
      onClick={() => handleSort(field)}
    >
      <div className={cn("flex items-center gap-2", className.includes('text-right') && "justify-end")}>
        {children}
        <ArrowUpDown className="h-4 w-4 opacity-50" />
      </div>
    </th>
  );

  const exportToCSV = () => {
    if (!data?.orders) return;

    const headers = [
      'Date',
      'Assembly Order',
      'Build SKU',
      'Build Description',
      'Technician',
      'Total Cost',
      'Reservation ID',
      'TO Number',
      'Started',
      'Duration'
    ];

    const rows = data.orders.map(order => [
      formatDate(order.completed_date),
      order.aso,
      order.build_sku,
      order.build_description,
      order.technician_name,
      order.total_cost.toFixed(2),
      order.reservation_id,
      order.to_number || '',
      formatDate(order.created_at),
      calculateBuildDuration(order.created_at, order.completed_date) || '',
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => 
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `posted-assembly-orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalPages = data?.totalPages || 1;
  const canGoPrevious = page > 1;
  const canGoNext = page < totalPages;

  if (contextLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Posted Assembly Orders</h1>
          <p className="text-muted-foreground mt-1">View and export completed build records</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-600" />
              <p className="font-medium text-red-600 mb-2">Error loading assembly orders</p>
              <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
              <div className="text-xs text-left max-w-2xl mx-auto bg-muted p-4 rounded">
                <p className="font-medium mb-2">Troubleshooting:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Check if the <code className="bg-background px-1 py-0.5 rounded">posted_assembly_orders</code> table exists</li>
                  <li>Verify database migration scripts have been run</li>
                  <li>Check browser console for detailed error</li>
                  <li>Run: <code className="bg-background px-1 py-0.5 rounded">SELECT COUNT(*) FROM posted_assembly_orders;</code></li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Posted Assembly Orders</h1>
          <p className="text-muted-foreground mt-1">
            {isTechnician ? 'Your completed build records' : isManager ? 'Completed build records for your managed technicians' : 'View and export completed build records'}
          </p>
        </div>
        {canViewCosts && (
          <Button onClick={exportToCSV} disabled={!data?.orders?.length}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className={cn("grid gap-4", canViewCosts ? "md:grid-cols-3" : "md:grid-cols-2")}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryData?.orderCount ?? '—'}</div>
            <p className="text-xs text-muted-foreground">Assembly orders posted</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Units</CardTitle>
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryData?.totalUnits ?? '—'}</div>
            <p className="text-xs text-muted-foreground">PCs assembled</p>
          </CardContent>
        </Card>
        {canViewCosts && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
              <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(summaryData?.totalCost || 0)}</div>
              <p className="text-xs text-muted-foreground">Component cost</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by order, build SKU, component SKU, technician..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                    // Expansion is relative to what the search auto-opens, so a
                    // toggle held over from the previous search would invert the
                    // wrong rows.
                    setExpandedOrders(new Set());
                  }}
                  className="pl-9 pr-9"
                />
                {search && (
                  <button
                    onClick={() => { setSearch(''); setPage(1); setExpandedOrders(new Set()); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                placeholder="From"
                className="w-36"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                placeholder="To"
                className="w-36"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-muted-foreground">Show:</span>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={pageSize}
                onChange={(e) => {
                  const val = e.target.value;
                  setPageSize(val === 'all' ? 'all' : Number(val));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-sm text-muted-foreground">Loading assembly orders...</p>
            </div>
          ) : !data?.orders?.length ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <FileSpreadsheet className="h-12 w-12 mb-4 opacity-50" />
              <p className="font-medium mb-2">No posted assembly orders found</p>
              <p className="text-sm">
                {search || dateFrom || dateTo
                  ? 'Try adjusting your filters'
                  : 'Completed builds will appear here once posted from the Production page'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="w-10 p-3"></th>
                      <SortHeader field="completed_date">Date</SortHeader>
                      <SortHeader field="aso">Assembly Order</SortHeader>
                      <th className="text-right p-3 font-medium whitespace-nowrap text-sm">Qty</th>
                      <SortHeader field="build_sku">Build SKU</SortHeader>
                      <SortHeader field="build_name">Build Name</SortHeader>
                      <SortHeader field="technician_name">Technician</SortHeader>
                      {canViewCosts && <SortHeader field="total_cost" className="text-right p-3">Build Cost</SortHeader>}
                      {isSuperAdmin && <th className="w-10 p-3"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {data.orders.map((order) => {
                      // An order found via one of its components opens by
                      // default — otherwise the row gives no hint of why the
                      // search matched it. The toggle then inverts that default
                      // rather than fighting it, so such a row can still be
                      // collapsed and an untagged one still expanded.
                      const matchedSkus = new Set(order.matched_components);
                      const autoExpanded = matchedSkus.size > 0;
                      const isExpanded = expandedOrders.has(order.id) !== autoExpanded;
                      return (
                      <React.Fragment key={order.id}>
                        {/* Main Row */}
                        <tr 
                          className={cn(
                            "border-b hover:bg-muted/50 transition-colors cursor-pointer",
                            isExpanded && "bg-muted/50"
                          )}
                          onClick={() => toggleExpanded(order.id)}
                        >
                          <td className="p-3 text-muted-foreground">
                            {isExpanded ? (
                              <ChevronDown className="h-5 w-5" />
                            ) : (
                              <ChevronRightIcon className="h-5 w-5" />
                            )}
                          </td>
                          <td className="p-3 text-sm whitespace-nowrap">
                            {order.completed_date ? formatDate(order.completed_date) : '—'}
                          </td>
                          <td className="p-3" onClick={e => e.stopPropagation()}>
                            <Link
                              to={`/stock-io/${order.id}`}
                              className="font-mono text-sm font-medium text-primary hover:underline"
                            >
                              {order.aso}
                            </Link>
                          </td>
                          <td className="p-3 text-right text-sm font-medium">{order.quantity}</td>
                          <td className="p-3">
                            <span className="font-mono text-sm">{order.build_sku || '—'}</span>
                            {/* Keeps the reason for the hit visible after the row is collapsed. */}
                            {order.matched_components.map(sku => (
                              <span
                                key={sku}
                                className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 text-[10px] font-mono align-middle"
                              >
                                {sku}
                              </span>
                            ))}
                          </td>
                          <td className="p-3 text-sm truncate max-w-[200px]" title={order.build_description}>
                            {order.build_description || '—'}
                          </td>
                          <td className="p-3 text-sm">{order.technician_name || '—'}</td>
                          {canViewCosts && (
                            <td className="p-3 text-sm text-right font-mono font-medium">
                              <div className="flex flex-col items-end">
                                <span>{formatCurrency(order.per_build_cost)}</span>
                                {order.quantity > 1 && (
                                  <span className="text-xs text-muted-foreground font-normal">
                                    {formatCurrency(order.total_cost)} total
                                  </span>
                                )}
                              </div>
                            </td>
                          )}
                          {isSuperAdmin && (
                            <td className="p-3" onClick={e => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                                disabled={deletePAO.isPending}
                                onClick={() => setDeleteModal(order)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          )}
                        </tr>
                        
                        {/* Expanded Components */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={isSuperAdmin ? 9 : canViewCosts ? 8 : 7} className="p-0 bg-muted/30">
                              <div className="p-4 pl-12">
                                {/* Metadata Badges - TOP */}
                                <div className="mb-3 flex flex-wrap items-center gap-4">
                                  {order.reservation_id && (
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-muted-foreground">Reservation:</span>
                                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-mono">
                                        {order.reservation_code || order.reservation_id}
                                      </span>
                                    </div>
                                  )}
                                  {order.to_number && (
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-muted-foreground">Transfer Order:</span>
                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-mono">
                                        {order.to_number}
                                      </span>
                                    </div>
                                  )}
                                  {(() => {
                                    const sns = order.serial_numbers;
                                    const entries = sns ? Object.values(sns).filter(Boolean) : [];
                                    const display = entries.length > 0 ? entries : (order.serial_number ? [order.serial_number] : []);
                                    if (display.length === 0) return null;
                                    return (
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs font-medium text-muted-foreground">S/N:</span>
                                        {display.map((sn, i) => (
                                          <span key={i} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-mono">
                                            {sn}
                                          </span>
                                        ))}
                                      </div>
                                    );
                                  })()}

                                  {order.created_at && order.completed_date && new Date(order.created_at) <= new Date(order.completed_date) && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs font-medium text-muted-foreground">Started:</span>
                                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-mono">
                                        {formatDate(order.created_at)}
                                      </span>
                                      <span className="text-xs font-medium text-muted-foreground">Completed:</span>
                                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-mono">
                                        {formatDate(order.completed_date)}
                                      </span>
                                      {calculateBuildDuration(order.created_at, order.completed_date) && (
                                        <span className="text-xs text-muted-foreground">
                                          ({calculateBuildDuration(order.created_at, order.completed_date)})
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Components Table */}
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b bg-muted/50">
                                      <th className="text-left p-2 font-medium">Type</th>
                                      <th className="text-left p-2 font-medium">Part Number</th>
                                      <th className="text-left p-2 font-medium">Description</th>
                                      <th className="text-right p-2 font-medium">Qty</th>
                                      {canViewCosts && <th className="text-right p-2 font-medium">Unit Cost</th>}
                                      {canViewCosts && <th className="text-right p-2 font-medium">Line Total</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {order.components.length > 0 ? (
                                      [...order.components].sort((a, b) => getComponentSortRank(a.part_type ?? '') - getComponentSortRank(b.part_type ?? '')).map((comp, idx) => (
                                        <tr
                                          key={idx}
                                          className={cn(
                                            "border-b last:border-0",
                                            matchedSkus.has(comp.part_number) && "bg-amber-100 dark:bg-amber-500/20"
                                          )}
                                        >
                                          <td className="p-2">
                                            {comp.part_type && comp.part_type !== 'COMPONENT' && (() => {
                                              const typeLabels: Record<string, string> = {
                                                CPU: 'CPU',
                                                FAN: 'CPU Cooler',
                                                MB:  'Motherboard',
                                                RAM: 'RAM',
                                                ROM: 'Storage',
                                                GPU: 'Graphics',
                                                PSU: 'PSU',
                                                OS:  'OS',
                                                CAS: 'Case',
                                                COO: 'Cooler',
                                              };
                                              const label = typeLabels[comp.part_type] ?? (comp.part_type?.startsWith('ROM') ? 'Storage' : comp.part_type?.startsWith('ACC') ? 'Accessory' : comp.part_type);
                                              return (
                                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground">
                                                  {label}
                                                </span>
                                              );
                                            })()}
                                          </td>
                                          <td className="p-2 font-mono text-xs">{comp.part_number}</td>
                                          <td className="p-2 text-xs truncate max-w-[250px]" title={comp.description}>
                                            {comp.description}
                                          </td>
                                        <td className="p-2 text-right">
                                            {Number.isInteger(comp.quantity) ? comp.quantity : comp.quantity.toFixed(1)}
                                          </td>
                                          {canViewCosts && <td className="p-2 text-right font-mono">{formatCurrency(comp.unit_cost)}</td>}
                                          {canViewCosts && <td className="p-2 text-right font-mono font-medium">{formatCurrency(comp.line_total)}</td>}
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan={canViewCosts ? 6 : 4} className="p-4 text-center text-muted-foreground text-xs">
                                          No components found for this assembly order
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                  {canViewCosts && order.components.length > 0 && (
                                    <tfoot>
                                      <tr className="bg-muted/50">
                                        <td colSpan={5} className="p-2 text-right font-medium">Build Cost:</td>
                                        <td className="p-2 text-right font-mono font-bold">
                                          {formatCurrency(order.per_build_cost)}
                                        </td>
                                      </tr>
                                    </tfoot>
                                  )}
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pageSize !== 'all' && totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, data.totalRecords)} of {data.totalRecords} records
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(1)}
                      disabled={!canGoPrevious}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p - 1)}
                      disabled={!canGoPrevious}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm px-2">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={!canGoNext}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(totalPages)}
                      disabled={!canGoNext}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      {/* Delete PAO Confirmation Modal */}
      <Dialog open={!!deleteModal} onOpenChange={(open) => { if (!open) setDeleteModal(null); }}>
        <DialogContent className="sm:max-w-lg overflow-y-auto max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Delete Posted Order {deleteModal?.aso}?</DialogTitle>
            <DialogDescription>
              Component stock will be restored. This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deleteModal?.reservation_id && deleteModal?.reservation_code ? (
            <>
              <div className="space-y-3">
                <p className="text-sm">
                  This PAO fulfilled <strong>{deleteModal.quantity} unit{deleteModal.quantity !== 1 ? 's' : ''}</strong> for
                  reservation <span className="font-mono font-medium text-primary">{deleteModal.reservation_code}</span>.
                  How should the reservation be handled?
                </p>

                <div className="grid gap-2">
                  <button
                    type="button"
                    className="w-full text-left border rounded-lg p-3 hover:bg-muted/50 transition-colors disabled:opacity-50"
                    disabled={deletePAO.isPending}
                    onClick={() => deletePAO.mutate({ order: deleteModal, mode: 'blank_slate' })}
                  >
                    <div className="font-medium text-sm">Blank Slate</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Remove {deleteModal.quantity} unit{deleteModal.quantity !== 1 ? 's' : ''} from the reservation — as if they were never ordered.
                    </div>
                  </button>
                  <button
                    type="button"
                    className="w-full text-left border rounded-lg p-3 hover:bg-muted/50 transition-colors disabled:opacity-50"
                    disabled={deletePAO.isPending}
                    onClick={() => deletePAO.mutate({ order: deleteModal, mode: 'restore' })}
                  >
                    <div className="font-medium text-sm">Restore to Queue</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Keep the original quantity, roll back fulfillment so those {deleteModal.quantity} unit{deleteModal.quantity !== 1 ? 's' : ''} go back into the build queue.
                    </div>
                  </button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDeleteModal(null)} disabled={deletePAO.isPending}>
                  Cancel
                </Button>
              </DialogFooter>
            </>
          ) : (
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteModal(null)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={deletePAO.isPending}
                onClick={() => deleteModal && deletePAO.mutate({ order: deleteModal, mode: 'blank_slate' })}
              >
                Delete
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
